import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Countdown } from '@pacer/shared';
import { MEMBER_COLORS } from '@pacer/shared';
import * as api from '../api';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Button, Field, Input, Textarea, cx } from './ui';
import { useZone } from '../lib/dates';
import { queryKeys } from '../state/queries';
import { confirmDelete, toast } from '../state/ui';

// Icons that suit a big event or trip. Kept in sync with the Icon set.
const PLAN_ICONS: IconName[] = [
  'plane',
  'gift',
  'cake',
  'star',
  'flag',
  'heart',
  'home',
  'sparkle',
  'sun',
  'activity',
  'school',
  'briefcase',
];

/** Create or edit a countdown / plan: date, look, whether it's pinned, and prep notes. */
export function CountdownEditor({ countdown, onClose }: { countdown: Countdown | null; onClose: () => void }) {
  const qc = useQueryClient();
  const zone = useZone();
  const [title, setTitle] = useState(countdown?.title ?? '');
  const [date, setDate] = useState(countdown?.targetDate ?? DateTime.now().setZone(zone).plus({ days: 30 }).toISODate()!);
  const [icon, setIcon] = useState<string | null>(countdown?.icon ?? 'star');
  const [color, setColor] = useState<string>(countdown?.color ?? MEMBER_COLORS[0]);
  const [pinned, setPinned] = useState(countdown?.pinned ?? false);
  const [notes, setNotes] = useState(countdown?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const done = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.countdowns });
    onClose();
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = { title: title.trim(), targetDate: date, icon, color, pinned, notes: notes.trim() || null };
      return countdown ? api.updateCountdown(countdown.id, payload) : api.createCountdown(payload);
    },
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteCountdown(countdown!.id),
    onSuccess: async () => {
      toast('Deleted', 'success');
      await done();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={countdown ? 'Edit' : 'New'}
      title={countdown ? 'Edit plan' : 'New plan'}
      footer={
        <>
          {countdown && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete(`“${countdown.title}”`, 'Its checklist goes too.')) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!title.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="What">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Cayman trip" autoCapitalize="sentences" />
        </Field>

        <Field label="When">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>

        <label className="flex touch items-center gap-3">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
          <span className="text-[14px] text-secondary">Pin to the dashboard with its checklist</span>
        </label>

        <Field label="Icon">
          <div className="flex flex-wrap gap-2 pt-1">
            {PLAN_ICONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={cx(
                  'flex size-10 items-center justify-center rounded-md border transition-colors',
                  icon === ic ? 'border-accent text-accent' : 'border-border-subtle text-secondary hover:text-primary',
                )}
                aria-label={ic}
              >
                <Icon name={ic} size={18} />
              </button>
            ))}
          </div>
        </Field>

        <Field label="Colour">
          <div className="flex flex-wrap gap-2 pt-1">
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className="size-9 rounded-full"
                style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 3 }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Field>

        <Field label="Notes" hint="Anything to remember — confirmation numbers, who's coming…">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
